import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { LecturaIngreso } from "@/componentes/ingresos/LecturaIngreso"
import { aplicacionesDe, RepartoCxc, useCxcAbiertas } from "@/componentes/ingresos/RepartoCxc"
import { SelectorPagador } from "@/componentes/ingresos/SelectorPagador"
import { cobraCxc, type Ingreso, type Pagador, type Reparto } from "@/componentes/ingresos/tipos"
import { precioValido } from "@/componentes/ordenes/lineas"
import { esCuentaBloqueada, PinEnLinea } from "@/componentes/tesoreria/CuentaBloqueada"
import type { Cuenta, Divisa } from "@/componentes/tesoreria/tipos"
import {
  type Adjuntando,
  AdjuntosNuevos,
  archivosPegados,
  sumar,
} from "@/componentes/ui/AdjuntosNuevos"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { tienePermiso } from "@/lib/autorizacion"
import { normalizarDecimal } from "@/lib/decimales"
import { usarSesion } from "@/lib/sesion"
import { Campo } from "@/paginas/interno/Gastos"

export function hoy(): string {
  const d = new Date()
  const dos = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`
}

/** Lo que una mutación de ingresos deja viejo. */
export const CLAVES_INGRESO = [
  ["ingresos"],
  ["movimientos"],
  ["resumen-cuenta"],
  ["cuentas-clientes"],
  ["cuenta-cliente"],
  ["cuentas-empleados"],
  ["cuenta-empleado"],
  ["saldos-proveedores"],
  ["estado-cuenta-proveedor"],
  ["cxc"],
] as const

/**
 * Reportar un ingreso: entra al banco al momento, con su soporte, y —si se sabe quién
 * lo pagó— cuenta también en la cuenta de esa persona.
 *
 * ⚠ **El pagador es opcional** (pedido del usuario): sin él el ingreso queda POR
 * DETERMINAR y se le asigna después desde la lista. Lo que no puede faltar es el
 * soporte: un ingreso tecleado sin su captura es la palabra de quien lo teclea.
 *
 * ⚠ **Con un cliente, se reparte entre sus CxC abiertas de la misma moneda** y lo que
 * sobra queda de anticipo. Con un proveedor, el servidor consume primero el saldo que
 * la empresa tenga con él; con un empleado, abona lo que debe.
 *
 * ⚠ **Una cuenta con PIN pide abrirla** si el servidor responde `CUENTA_BLOQUEADA`, y
 * el reporte se reintenta solo al abrirla.
 */
export function DialogoIngreso({ onCerrar }: { onCerrar: () => void }) {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const [cuentaId, setCuentaId] = useState<number | null>(null)
  const [monto, setMonto] = useState("")
  const [fecha, setFecha] = useState(hoy())
  const [referencia, setReferencia] = useState("")
  const [concepto, setConcepto] = useState("")
  const [pagador, setPagador] = useState<Pagador | null>(null)
  const [montos, setMontos] = useState<Reparto>({})
  const [soporte, setSoporte] = useState<Adjuntando[]>([])
  const [rechazados, setRechazados] = useState<string[]>([])

  const cuentas = useQuery({
    queryKey: ["cuentas-bancarias"],
    queryFn: () => api.get<Cuenta[]>("/api/v1/cuentas-bancarias"),
  })
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
  })
  const activas = (cuentas.data ?? []).filter((c) => c.activo)
  const cuenta = activas.find((c) => c.id === cuentaId) ?? null
  const divisa = (divisas.data ?? []).find((d) => d.id === cuenta?.divisa_id)?.codigo
  const valido = precioValido(monto)
  const conReparto = pagador?.tipo === "CLIENTE" && cobraCxc(divisa)

  const abiertas = useCxcAbiertas(conReparto && pagador ? pagador.id : null, divisa, valido, fecha)
  const aplicaciones = conReparto ? aplicacionesDe(montos, abiertas.data ?? [], divisa) : []

  const listo =
    cuenta !== null &&
    valido !== null &&
    fecha !== "" &&
    concepto.trim().length >= 3 &&
    soporte.length === 1 &&
    aplicaciones !== null &&
    // Sin las CxC cargadas no hay reparto que mandar: saldría todo de anticipo.
    (!conReparto || abiertas.data !== undefined)

  const reportar = useMutation({
    meta: { exito: "Ingreso reportado" },
    mutationFn: () => {
      const datos = new FormData()
      datos.append(
        "datos",
        JSON.stringify({
          cuenta_id: cuentaId,
          monto: valido,
          fecha_valor: fecha,
          concepto: concepto.trim(),
          referencia: referencia.trim() || null,
          pagador: pagador ? { tipo: pagador.tipo, id: pagador.id } : null,
          aplicaciones: aplicaciones ?? [],
        }),
      )
      if (soporte[0]) datos.append("soporte", soporte[0].archivo)
      return api.post<Ingreso>("/api/v1/ingresos", datos)
    },
    onSuccess: () => {
      for (const clave of CLAVES_INGRESO) clienteQuery.invalidateQueries({ queryKey: [...clave] })
      onCerrar()
    },
  })
  const bloqueada = esCuentaBloqueada(reportar.error)

  function adjuntar(archivos: File[]) {
    const siguiente = sumar([], archivos.slice(0, 1))
    setSoporte(siguiente.adjuntos)
    setRechazados(siguiente.rechazados)
  }

  return (
    <Modal abierto titulo="Reportar ingreso" onCerrar={onCerrar} ancho="lg">
      <form
        className="space-y-4"
        onSubmit={(evento) => {
          evento.preventDefault()
          if (listo) reportar.mutate()
        }}
        onPaste={(evento) => {
          const archivos = archivosPegados(evento.clipboardData, 0)
          if (archivos.length === 0) return
          evento.preventDefault()
          adjuntar(archivos)
        }}
      >
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Comprobante del ingreso</legend>
          <AdjuntosNuevos
            adjuntos={soporte}
            rechazados={rechazados}
            etiqueta="Adjuntar comprobante"
            multiple={false}
            onArchivos={adjuntar}
            onQuitar={() => setSoporte([])}
          />
          {soporte[0] && tienePermiso(usuario, "factura_ia:usar") && (
            <LecturaIngreso
              archivo={soporte[0].archivo}
              onUsar={(r) => {
                const c = r.comprobante
                const sugerida = r.cuenta_sugerida?.id ?? cuentaId
                if (r.cuenta_sugerida) setCuentaId(r.cuenta_sugerida.id)
                const moneda = (divisas.data ?? []).find(
                  (d) => d.id === activas.find((a) => a.id === sugerida)?.divisa_id,
                )?.codigo
                if (c.monto && (c.moneda === null || moneda === undefined || c.moneda === moneda))
                  setMonto(normalizarDecimal(c.monto) ?? c.monto)
                if (c.fecha) setFecha(c.fecha)
                if (c.referencia) setReferencia(c.referencia)
                if (c.concepto && !concepto.trim()) setConcepto(c.concepto.slice(0, 200))
                if (r.pagador_sugerido)
                  setPagador({ ...r.pagador_sugerido, documento: c.ordenante_id })
              }}
            />
          )}
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo para="ingreso-cuenta" etiqueta="Cuenta donde entró">
            <SelectorBuscable
              id="ingreso-cuenta"
              opciones={activas.map((c) => ({
                valor: c.id,
                etiqueta: c.nombre,
                detalle: [
                  (divisas.data ?? []).find((d) => d.id === c.divisa_id)?.codigo,
                  c.banco,
                  c.ultimos4 ? `···${c.ultimos4}` : null,
                ]
                  .filter(Boolean)
                  .join(" "),
              }))}
              valor={cuentaId}
              onCambiar={setCuentaId}
              placeholder="Buscar cuenta…"
            />
          </Campo>
          <Campo para="ingreso-monto" etiqueta={`Monto${divisa ? ` (${divisa})` : ""}`}>
            <input
              id="ingreso-monto"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              inputMode="decimal"
              className="campo mt-1 tabular-nums"
            />
          </Campo>
          <Campo para="ingreso-fecha" etiqueta="Fecha valor">
            <input
              id="ingreso-fecha"
              type="date"
              value={fecha}
              max={hoy()}
              onChange={(e) => setFecha(e.target.value)}
              className="campo mt-1"
            />
          </Campo>
          <Campo para="ingreso-referencia" etiqueta="Referencia bancaria">
            <input
              id="ingreso-referencia"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              maxLength={60}
              className="campo mt-1"
            />
          </Campo>
        </div>
        <Campo para="ingreso-concepto" etiqueta="Concepto">
          <input
            id="ingreso-concepto"
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
            maxLength={200}
            placeholder="Pago de la factura 123, anticipo del expediente…"
            className="campo mt-1"
          />
        </Campo>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Quién pagó</legend>
          <SelectorPagador
            valor={pagador}
            onCambiar={(p) => {
              setPagador(p)
              setMontos({})
            }}
          />
          {pagador?.tipo === "CLIENTE" && cuenta && !cobraCxc(divisa) && (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              Una CxC es en dólares o en bolívares: este ingreso en {divisa} queda entero de
              anticipo.
            </p>
          )}
          {conReparto && pagador && divisa && (
            <RepartoCxc
              abiertas={abiertas}
              divisa={divisa}
              monto={valido}
              reparto={montos}
              onReparto={setMontos}
            />
          )}
          {pagador?.tipo === "PROVEEDOR" && (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              Primero cubre el saldo que la empresa tenga con él en {divisa ?? "esa moneda"}; lo que
              pase de ahí queda como reembolso en su estado de cuenta.
            </p>
          )}
          {pagador?.tipo === "EMPLEADO" && (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              Abona lo que debe por anticipos y préstamos.
            </p>
          )}
        </fieldset>

        {/* El PIN se teclea aquí mismo y, abierta, se reporta (19/10): un segundo modal
            encima se cerraría junto con éste al pulsar Escape. */}
        <PinEnLinea error={reportar.error} onAbierta={() => reportar.mutate()} />
        {reportar.error instanceof ErrorApi && !bloqueada && (
          <p className="text-sm" style={{ color: "var(--destructive)" }}>
            {reportar.error.message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton type="submit" variante="primario" disabled={!listo || reportar.isPending}>
            {reportar.isPending ? "Reportando…" : "Reportar ingreso"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
